/*
* Association search view for authenticated users
*/

import '@progress/kendo-ui';

import { Grid } from '@progress/kendo-grid-react-wrapper';
import * as _ from 'lodash';
import * as React from 'react';
import { Row } from 'react-bootstrap';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import { compose } from 'redux';
import { connectRequest } from 'redux-query';

import * as Actions from '../actions';
import * as AppComp from '../common/app-component-base';
import { BootstrapColXl as Col } from '../common/bootstrap/bootstrapcolxl';
import { Formatters } from '../common/formatters';
import { GridUtilities } from '../common/grid/gridutilities';
import { NavigationLinks } from '../common/navigation';
import SearchInput from '../common/searchInput';
import { WhiteBoxPageSection } from '../common/whiteboxpagesection';
import ApiEndpoints from '../config/apiendpoints';
import * as ReduxQueries from '../queries';

class AssociationSearch extends AppComp.AppComponent {
    public gridOptions = GridUtilities.kendoGridOptionsPaging(
        [
            GridUtilities.kendoGridColumnDefinition('associationId', this.appTextGridColumnHeader('association-id'), 60, 1),
            (() => {
                const associationNameLinkTemplate = GridUtilities.kendoGridLinkColumnTemplate('associationName', 'associationDetailLinkHref', this.appText('association-detail-link-title'));
                return GridUtilities.kendoGridColumnDefinition('associationName', this.appTextGridColumnHeader('association-name'), 250, 2, associationNameLinkTemplate);
            })(),
            GridUtilities.kendoGridColumnDefinition('associationLocation', this.appTextGridColumnHeader('location'), 200, 3),
            GridUtilities.kendoGridColumnDefinition('viewInventoryLink', '', 50, 12, GridUtilities.kendoGridLinkColumnTemplate('viewInventoryLink', 'viewInventoryLinkHref', this.appText('view-inventory-link-title')), { headerTemplate: '' }),
            GridUtilities.kendoGridColumnDefinition('viewOrderLink', '', 50, 13, GridUtilities.kendoGridLinkColumnTemplate('viewOrderLink', 'viewOrderLinkHref', this.appText('view-order-link-title')), { headerTemplate: '' }),
        ],
        this.appText('no-results'), {}, this.appTextPageTitle()
    );

    public constructor(props: any) {
        super(props, 'app.association-search.');
    }

    public extendGridData(records: Array<any>) {
        // Extend search result records with additional dynamic field values for use in grid
        if (!records) { return []; }

        return records.map(r => {
            let record = Object.assign({}, r);

            // Association Location
            record.associationLocation = Formatters.composeLocationCityState(record.location);

            // Association name
            record.associationName = '';
            if (record.name) {
                record.associationName = record.name;
            }

            // Association name link details
            record.associationDetailLinkHref = '';
            if (record.associationId) {
                record.associationDetailLinkHref = NavigationLinks.associationDetail(record.associationId);
            }

            // View inventory link
            record.viewInventoryLink = '';
            record.viewInventoryLinkHref = '';
            if (record.associationId) {
                record.viewInventoryLink = this.appText('view-inventory');
                record.viewInventoryLinkHref = NavigationLinks.associationInventory(record.associationId);
            }

            // View order link            
            record.viewOrderLink = '';
            record.viewOrderLinkHref = '';
            record.viewOrderLink = this.appText('view-order');
            record.viewOrderLinkHref = NavigationLinks.associationViewOrder(record.associationId);

            return record;
        });
    }

    public handleSearchFilter(e: any) {
        const keyword = e.target.value;
        this.props.dispatch(Actions.searchAssociationsKeyword(keyword));
    }

    public handleSearchAssociationIdFilter(e: any) {
        const assocId = Formatters.getStr(e, 'target.value');
        this.props.dispatch(Actions.searchAssociationsAssociationId(assocId));
    }

    public render() {
        const { search, ...rest } = this.props;

        const rows = this.extendGridData(search.results);
        const dataSource = GridUtilities.kendoGridDataSourcePaged(rows);

        const pageTitle = this.appTextResultsAndPageTitle(rows.length);
        const page = (
            <WhiteBoxPageSection title={pageTitle}>
                <Row>
                    <Col xs={12}>
                        <form className="form-horizontal" onSubmit={e => { e.preventDefault(); }}>
                            <div className="form-group">
                                <Col xs={1} md={2} xl={1}>
                                    <label className="control-label">{this.appText('search-keyword')}</label>
                                </Col>
                                <Col xs={2} md={4} xl={2}>
                                    <SearchInput
                                        debounceTimeout={1000}
                                        onChange={(e) => { this.handleSearchFilter(e); }}
                                        placeholder={this.appText('enter-keyword')}
                                        value={search.term}
                                    />
                                </Col>

                                <Col xs={1} md={2} xl={1}>
                                    <label className="control-label">{this.appText('search-assoc-id')}</label>
                                </Col>
                                <Col xs={2} md={4} xl={2}>
                                    <SearchInput
                                        debounceTimeout={1000}
                                        onChange={(e) => { this.handleSearchAssociationIdFilter(e); }}
                                        placeholder={this.appText('enter-id')}
                                        value={search.associationId}
                                    />
                                </Col>
                            </div>
                        </form>
                    </Col>
                </Row>

                <Row>
                    <Col xs={12}>
                        <Grid dataSource={dataSource} {...this.gridOptions} />
                    </Col>
                </Row>
            </WhiteBoxPageSection>
        );

        return (
            <AppComp.RenderPageWithLayout branding={this.props.branding} pageName={pageTitle} {...rest}>
                {page}
            </AppComp.RenderPageWithLayout>
        );
    }
}

const getApiUrl = () => ApiEndpoints.getSearchAssociationsUrl();

const mapPropsToConfigs = (props) => ReduxQueries.Queries.searchAssociationsRequest(
    getApiUrl(),
    ReduxQueries.PropsAccessToken(props),
    _.get(props, 'search.term', ''),
    _.get(props, 'search.associationId'), // , -1),
    _.get(props, 'search.operator', '')
);

const mapStateToProps = (state, props) => {
    return ReduxQueries.MapQueryStateToPropsByUrl(getApiUrl(), state, props, {
        search: {
            operator: 'OR',
            results: state.entities.searchAssociationsResults,
            term: state.searchFiltersAssociations.keyword,
            associationId: state.searchFiltersAssociations.associationId,
        }
    });
};

const AssociationSearchContainer = compose<any>(
    connect(mapStateToProps),
    connectRequest(mapPropsToConfigs)
)(AssociationSearch);

export default withRouter(AssociationSearchContainer);



// WEBPACK FOOTER //
// ./src/association-search/index.tsx